/**
 * Charge Regulator Calculator
 * Recommends a charge controller based on battery voltage, solar panel model,
 * panel quantity, or custom panel power/voltage ranges.
 * Reads assets/charge-regulator-en.csv (or a language-specific CSV).
 */

(function () {
  'use strict';

  // ============================================================
  // CSV Parser (robust, handles quoted fields)
  // ============================================================
  function parseCsv(text) {
    const rows = [];
    let field = '';
    let row = [];
    let insideQuotes = false;

    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (c === '"') {
        if (insideQuotes && text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          insideQuotes = !insideQuotes;
        }
      } else if (c === ',' && !insideQuotes) {
        row.push(field.trim());
        field = '';
      } else if ((c === '\n' || c === '\r') && !insideQuotes) {
        if (c === '\r' && text[i + 1] === '\n') i++;
        row.push(field.trim());
        if (row.some(v => v !== '')) rows.push(row);
        field = '';
        row = [];
      } else {
        field += c;
      }
    }

    if (field.length || row.length) {
      row.push(field.trim());
      if (row.some(v => v !== '')) rows.push(row);
    }

    return rows;
  }

  function escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function sortByRangeStart(values) {
    return values.slice().sort((a, b) => {
      const numA = parseInt(String(a).match(/\d+/)?.[0] || '0', 10);
      const numB = parseInt(String(b).match(/\d+/)?.[0] || '0', 10);
      return numA - numB;
    });
  }

  const DEFAULT_TEXT = {
    placeholderText: 'Select...',
    voltageLabel: 'Battery bank voltage',
    panelModelLabel: 'Solar panel model',
    panelQuantityLabel: 'Panel quantity',
    customPowerLabel: 'Total panel power',
    customVoltageLabel: 'Total panel voltage',
    calculateLabel: 'Find regulator',
    addToCartLabel: 'Add to cart',
    resultTitle: 'Recommended regulator',
    resultSummary: 'Configuration',
    quantityLabel: 'Qty',
    errorInitPrefix: 'Initialization error:',
    errorCsvMissing: 'CSV file is not configured',
    errorCsvEmpty: 'CSV file is empty or invalid',
    errorIncomplete: 'Please complete every field.',
    errorNoResults: 'No regulator found for this configuration.',
    errorNoCartItems: 'This product cannot be added to cart.'
  };

  // ============================================================
  // ChargeRegulatorCalculator Class
  // ============================================================
  class ChargeRegulatorCalculator {
    constructor(rootElement) {
      this.root = rootElement;
      this.sectionId = rootElement.dataset.sectionId;
      this.csvData = {
        known: {},
        custom: {},
        knownModels: {},
        customPowerOptions: {},
        customVoltageOptions: {}
      };
      this.skuMap = {};
      this.text = this.loadTextConfig();
      this.cartItem = null;

      this.settings = {
        csvUrl: rootElement.dataset.csvUrl,
        placeholderText: this.text.placeholderText || rootElement.dataset.placeholderText || 'Select...',
        openLinksNewTab: rootElement.dataset.openLinksNewTab === 'true',
        text: this.text,
        panelCustomValue: rootElement.dataset.panelCustomValue || 'custom'
      };

      // DOM references
      this.errorDisplay = this.root.querySelector(`#charge-regulator-error-${this.sectionId}`);
      this.form = this.root.querySelector(`#charge-regulator-form-${this.sectionId}`);
      this.voltageSelect = this.root.querySelector(`#charge-regulator-voltage-${this.sectionId}`);
      this.panelSelect = this.root.querySelector(`#charge-regulator-panel-${this.sectionId}`);
      this.panelQtySelect = this.root.querySelector(`#charge-regulator-panel-qty-${this.sectionId}`);
      this.customPowerSelect = this.root.querySelector(`#charge-regulator-custom-power-${this.sectionId}`);
      this.customVoltageSelect = this.root.querySelector(`#charge-regulator-custom-voltage-${this.sectionId}`);
      this.calculateBtn = this.root.querySelector(`#charge-regulator-calculate-${this.sectionId}`);
      this.resultsContainer = this.root.querySelector(`#charge-regulator-results-${this.sectionId}`);
      this.cartAction = this.root.querySelector(`#charge-regulator-cart-action-${this.sectionId}`);
      this.addToCartBtn = this.root.querySelector(`#charge-regulator-add-to-cart-${this.sectionId}`);

      this.init();
    }

    async init() {
      try {
        await this.loadCsv();
        this.loadSkuMap();
        this.populateStaticOptions();
        this.bindEvents();
        this.updateConditionalFields();
      } catch (e) {
        console.error('[ChargeRegulatorCalculator] Init error:', e);
        this.showError(`${this.text.errorInitPrefix} ${e.message}`);
      }
    }

    loadTextConfig() {
      const script = this.root.querySelector(`#charge-regulator-text-config-${this.sectionId}`);
      if (script && script.textContent) {
        try {
          return { ...DEFAULT_TEXT, ...JSON.parse(script.textContent) };
        } catch (e) {
          console.warn('[ChargeRegulatorCalculator] Invalid text configuration', e);
        }
      }
      return { ...DEFAULT_TEXT };
    }

    async loadCsv() {
      if (!this.settings.csvUrl || this.settings.csvUrl.endsWith('/')) {
        throw new Error(this.text.errorCsvMissing);
      }

      const res = await fetch(this.settings.csvUrl, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const text = await res.text();
      const parsed = parseCsv(text);

      if (parsed.length < 2) throw new Error(this.text.errorCsvEmpty);

      const dataRows = parsed.slice(1).filter(r => r[0] && r[0].trim() !== '');

      dataRows.forEach(row => {
        const voltage = row[0]?.trim() || '';
        const panelModel = row[1]?.trim() || '';
        const panelQty = row[2]?.trim() || '';
        const customPower = row[3]?.trim() || '';
        const customVoltage = row[4]?.trim() || '';
        const recQty = row[5]?.trim() || '';
        const recSku = row[6]?.trim() || '';

        if (!voltage || !panelModel || !recQty || !recSku) return;

        if (panelModel === this.settings.panelCustomValue) {
          // Custom panel path
          if (!customPower || !customVoltage) return;
          if (!this.csvData.custom[voltage]) this.csvData.custom[voltage] = {};
          if (!this.csvData.custom[voltage][customPower]) this.csvData.custom[voltage][customPower] = {};
          this.csvData.custom[voltage][customPower][customVoltage] = { qty: parseInt(recQty, 10), sku: recSku };

          if (!this.csvData.customPowerOptions[voltage]) this.csvData.customPowerOptions[voltage] = new Set();
          if (!this.csvData.customVoltageOptions[voltage]) this.csvData.customVoltageOptions[voltage] = new Set();
          this.csvData.customPowerOptions[voltage].add(customPower);
          this.csvData.customVoltageOptions[voltage].add(customVoltage);
        } else {
          // Known panel path
          if (!panelQty) return;
          if (!this.csvData.known[voltage]) this.csvData.known[voltage] = {};
          if (!this.csvData.known[voltage][panelModel]) this.csvData.known[voltage][panelModel] = {};
          this.csvData.known[voltage][panelModel][panelQty] = { qty: parseInt(recQty, 10), sku: recSku };

          if (!this.csvData.knownModels[voltage]) this.csvData.knownModels[voltage] = new Set();
          this.csvData.knownModels[voltage].add(panelModel);
        }
      });
    }

    loadSkuMap() {
      const script = this.root.querySelector(`#charge-regulator-sku-map-${this.sectionId}`);
      if (script && script.textContent) {
        try {
          this.skuMap = JSON.parse(script.textContent);
        } catch (e) {
          console.warn('[ChargeRegulatorCalculator] Invalid SKU map', e);
          this.skuMap = {};
        }
      }
    }

    populateStaticOptions() {
      const ph = `<option value="">${escapeHtml(this.settings.placeholderText)}</option>`;

      // Voltage options from available data
      const voltages = Object.keys(this.csvData.known).concat(Object.keys(this.csvData.custom));
      const uniqueVoltages = [...new Set(voltages)].sort();
      if (this.voltageSelect) {
        this.voltageSelect.innerHTML = ph + uniqueVoltages.map(v => `<option value="${escapeHtml(v)}">${escapeHtml(v)}</option>`).join('');
      }

      // Panel models are rendered dynamically per voltage
      if (this.panelSelect) this.panelSelect.innerHTML = ph;

      // Quantity and custom selects start empty
      if (this.panelQtySelect) this.panelQtySelect.innerHTML = ph;
      if (this.customPowerSelect) this.customPowerSelect.innerHTML = ph;
      if (this.customVoltageSelect) this.customVoltageSelect.innerHTML = ph;
    }

    bindEvents() {
      this.voltageSelect?.addEventListener('change', () => {
        this.panelSelect.value = '';
        this.panelQtySelect.value = '';
        this.customPowerSelect.value = '';
        this.customVoltageSelect.value = '';
        this.populatePanelOptions();
        this.updateConditionalFields();
        this.clearResults();
      });

      this.panelSelect?.addEventListener('change', () => {
        this.panelQtySelect.value = '';
        this.customPowerSelect.value = '';
        this.customVoltageSelect.value = '';
        this.populateKnownQuantityOptions();
        this.populateCustomOptions();
        this.updateConditionalFields();
        this.clearResults();
      });

      [this.panelQtySelect, this.customPowerSelect, this.customVoltageSelect].forEach(el => {
        el?.addEventListener('change', () => {
          this.clearResults();
        });
      });

      this.calculateBtn?.addEventListener('click', () => this.calculate());
      this.addToCartBtn?.addEventListener('click', () => this.addToCart());
    }

    populatePanelOptions() {
      const ph = `<option value="">${escapeHtml(this.settings.placeholderText)}</option>`;
      const voltage = this.voltageSelect?.value;

      if (!voltage || !this.panelSelect) {
        if (this.panelSelect) this.panelSelect.innerHTML = ph;
        return;
      }

      const known = this.csvData.known[voltage] || {};
      const hasCustom = !!this.csvData.custom[voltage];
      const models = Object.keys(known).sort();

      let html = ph;
      models.forEach(model => {
        const label = this.getPanelDisplayName(model);
        html += `<option value="${escapeHtml(model)}">${escapeHtml(label)}</option>`;
      });

      if (hasCustom) {
        html += `<option value="${escapeHtml(this.settings.panelCustomValue)}">${escapeHtml(this.text.customPanelOptionLabel || 'I have another panel model')}</option>`;
      }

      this.panelSelect.innerHTML = html;
    }

    populateKnownQuantityOptions() {
      const ph = `<option value="">${escapeHtml(this.settings.placeholderText)}</option>`;
      const voltage = this.voltageSelect?.value;
      const model = this.panelSelect?.value;

      if (!voltage || !model || model === this.settings.panelCustomValue || !this.panelQtySelect) {
        if (this.panelQtySelect) this.panelQtySelect.innerHTML = ph;
        return;
      }

      const qtyMap = this.csvData.known[voltage]?.[model] || {};
      const quantities = Object.keys(qtyMap).map(q => parseInt(q, 10)).sort((a, b) => a - b);

      let html = ph;
      quantities.forEach(q => {
        html += `<option value="${q}">${q}</option>`;
      });

      this.panelQtySelect.innerHTML = html;
    }

    populateCustomOptions() {
      const ph = `<option value="">${escapeHtml(this.settings.placeholderText)}</option>`;
      const voltage = this.voltageSelect?.value;
      const model = this.panelSelect?.value;

      if (!voltage || model !== this.settings.panelCustomValue) {
        if (this.customPowerSelect) this.customPowerSelect.innerHTML = ph;
        if (this.customVoltageSelect) this.customVoltageSelect.innerHTML = ph;
        return;
      }

      const powerOptions = sortByRangeStart(Array.from(this.csvData.customPowerOptions[voltage] || []));
      const voltageOptions = sortByRangeStart(Array.from(this.csvData.customVoltageOptions[voltage] || []));

      if (this.customPowerSelect) {
        this.customPowerSelect.innerHTML = ph + powerOptions.map(o => `<option value="${escapeHtml(o)}">${escapeHtml(o)}</option>`).join('');
      }
      if (this.customVoltageSelect) {
        this.customVoltageSelect.innerHTML = ph + voltageOptions.map(o => `<option value="${escapeHtml(o)}">${escapeHtml(o)}</option>`).join('');
      }
    }

    updateConditionalFields() {
      const model = this.panelSelect?.value;
      const isKnown = model && model !== this.settings.panelCustomValue;
      const isCustom = model === this.settings.panelCustomValue;

      this.root.querySelectorAll('[data-conditional-group="known-panel"]').forEach(el => {
        el.classList.toggle('is-hidden', !isKnown);
        el.classList.toggle('is-visible', isKnown);
      });
      this.root.querySelectorAll('[data-conditional-group="custom-panel"]').forEach(el => {
        el.classList.toggle('is-hidden', !isCustom);
        el.classList.toggle('is-visible', isCustom);
      });
    }

    getPanelDisplayName(model) {
      // Prefer the SKU map (product title in the current locale), fallback to panel config, then raw SKU.
      if (this.skuMap && this.skuMap[model]?.title) {
        return this.skuMap[model].title;
      }
      const configScript = this.root.querySelector(`#charge-regulator-panel-config-${this.sectionId}`);
      if (configScript && configScript.textContent) {
        try {
          const config = JSON.parse(configScript.textContent);
          if (config[model]?.name) return config[model].name;
        } catch (e) {
          console.warn('[ChargeRegulatorCalculator] Invalid panel configuration', e);
        }
      }
      return model;
    }

    isComplete() {
      const voltage = this.voltageSelect?.value;
      const model = this.panelSelect?.value;
      if (!voltage || !model) return false;

      if (model === this.settings.panelCustomValue) {
        return !!this.customPowerSelect?.value && !!this.customVoltageSelect?.value;
      }

      return !!this.panelQtySelect?.value;
    }

    calculate() {
      this.hideError();

      if (!this.isComplete()) {
        this.showError(this.text.errorIncomplete);
        this.clearResults();
        return;
      }

      const voltage = this.voltageSelect.value;
      const model = this.panelSelect.value;
      let result = null;
      let configLabel = '';

      if (model === this.settings.panelCustomValue) {
        const power = this.customPowerSelect.value;
        const vRange = this.customVoltageSelect.value;
        result = this.csvData.custom[voltage]?.[power]?.[vRange] || null;
        configLabel = `${this.text.customPowerLabel || 'Power'}: ${power}, ${this.text.customVoltageLabel || 'Voltage'}: ${vRange}`;
      } else {
        const qty = this.panelQtySelect.value;
        result = this.csvData.known[voltage]?.[model]?.[qty] || null;
        configLabel = `${this.getPanelDisplayName(model)} × ${qty}`;
      }

      if (!result) {
        this.showError(this.text.errorNoResults);
        this.clearResults();
        return;
      }

      this.renderResult(result, voltage, configLabel);
      this.buildCartData(result);

      this.resultsContainer.style.display = 'block';
      if (this.cartAction) this.cartAction.style.display = 'flex';

      if (this.root.dataset.enableSmoothScroll === 'true') {
        setTimeout(() => {
          this.resultsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }

    renderResult(result, voltage, configLabel) {
      const tgt = this.settings.openLinksNewTab ? ' target="_blank" rel="noopener"' : '';
      const prodInfo = this.skuMap[result.sku] || {};
      const title = prodInfo.title || result.sku;
      const url = prodInfo.productUrl || '#';
      const hasImage = prodInfo.imageUrl && !prodInfo.imageUrl.startsWith('Liquid error');
      const isLinked = url !== '#';
      const linkAttrs = isLinked ? ` href="${escapeHtml(url)}"${tgt}` : '';

      const imgHtml = hasImage
        ? `<img class="charge-regulator-item-image img-fit" src="${escapeHtml(prodInfo.imageUrl)}" alt="${escapeHtml(title)}" loading="lazy">`
        : `<div class="charge-regulator-item-image-placeholder">${escapeHtml(this.text.imagePlaceholderLabel || 'Image')}</div>`;

      this.resultsContainer.innerHTML = `
        <div class="charge-regulator-result-card">
          <div class="charge-regulator-result-header">
            <h4 class="charge-regulator-result-title">${escapeHtml(this.text.resultTitle || 'Recommended regulator')}</h4>
            <span class="charge-regulator-result-subtitle">${escapeHtml(this.text.resultSummary || 'Configuration')}: ${escapeHtml(voltage)} - ${escapeHtml(configLabel)}</span>
          </div>
          <ul class="charge-regulator-result-items">
            <li class="charge-regulator-result-item">
              <div class="charge-regulator-item-media">
                ${isLinked ? `<a${linkAttrs} class="media block relative">` : '<div class="media block relative">'}
                  ${imgHtml}
                ${isLinked ? '</a>' : '</div>'}
              </div>
              <div class="charge-regulator-item-details">
                ${isLinked
                  ? `<a class="charge-regulator-item-title" ${linkAttrs}>${escapeHtml(title)}</a>`
                  : `<span class="charge-regulator-item-title">${escapeHtml(title)}</span>`}
                <span class="charge-regulator-item-subtitle">SKU: ${escapeHtml(result.sku)}</span>
                <span class="charge-regulator-item-qty">${escapeHtml(this.text.quantityLabel || 'Qty')}: ${result.qty}</span>
                ${isLinked ? `<a class="charge-regulator-item-view" ${linkAttrs}>${escapeHtml(this.text.viewProductLabel || 'View product')}</a>` : ''}
              </div>
            </li>
          </ul>
        </div>
      `;
    }

    buildCartData(result) {
      const prodInfo = this.skuMap[result.sku] || {};
      if (!prodInfo.variantId) {
        this.cartItem = null;
        return;
      }
      this.cartItem = { variantId: prodInfo.variantId, qty: result.qty };
    }

    async addToCart() {
      if (!this.cartItem) {
        this.showError(this.text.errorNoCartItems);
        return;
      }

      const cartDrawer = document.querySelector('cart-drawer');
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      const btn = this.addToCartBtn;

      // Build sections string the same way the theme product-form does.
      let sections = 'cart-icon-bubble';
      if (cartDrawer) {
        sections += `,${cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      const formData = new FormData();
      formData.append('id', this.cartItem.variantId);
      formData.append('quantity', this.cartItem.qty);
      formData.append('sections', sections);
      formData.append('sections_url', window.location.pathname);

      if (btn) {
        btn.setAttribute('aria-disabled', 'true');
        btn.classList.add('is-loading');
      }

      try {
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });

        const data = await response.json();

        if (!response.ok || data.status) {
          const error = typeof data.description === 'string'
            ? data.description
            : (data.message || 'Add to cart failed');
          throw new Error(error);
        }

        // Update cart icon count.
        if (cartIconBubble && data.sections && data.sections['cart-icon-bubble']) {
          cartIconBubble.innerHTML = data.sections['cart-icon-bubble'];
        }

        // Update cart drawer contents and open it.
        if (cartDrawer) {
          cartDrawer.renderContents(data, true);
        } else {
          // Fallback if cart drawer is not present.
          window.location.href = `${theme.routes.cart}?storefront=true`;
        }

        if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
          window.navigator.vibrate(100);
        }
      } catch (error) {
        console.error('[ChargeRegulatorCalculator] Add to cart error:', error);
        this.showError(error.message || this.text.errorNoCartItems);
        if (cartDrawer) cartDrawer.refresh(true);
      } finally {
        if (btn) {
          btn.removeAttribute('aria-disabled');
          btn.classList.remove('is-loading');
          btn.classList.add('is-success');
          setTimeout(() => btn.classList.remove('is-success'), 1400);
        }
      }
    }

    clearResults() {
      if (this.resultsContainer) {
        this.resultsContainer.innerHTML = '';
        this.resultsContainer.style.display = 'none';
      }
      if (this.cartAction) this.cartAction.style.display = 'none';
      this.cartItem = null;
    }

    showError(msg) {
      if (this.errorDisplay) {
        this.errorDisplay.textContent = msg;
        this.errorDisplay.style.display = 'block';
      }
    }

    hideError() {
      if (this.errorDisplay) {
        this.errorDisplay.textContent = '';
        this.errorDisplay.style.display = 'none';
      }
    }
  }

  // ============================================================
  // Initialize on DOM ready
  // ============================================================
  function init() {
    document.querySelectorAll('[data-charge-regulator-calculator]').forEach(container => {
      new ChargeRegulatorCalculator(container);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
